/**
 * Field fixes G2.3: "They may pick more than one" (`step.many`), as the composer edits it.
 *
 * Mirrors the sidecar's `tasks/many.ts` `manyRange` and `tasks/validate.ts` `many_range`: an
 * absent bound is the widest (at least 1, at most every choice), and a bound that IS the widest
 * is not written, so a question keeps meaning "any number of them" when a choice is added. The
 * server remains the one that refuses.
 */
/** How many choices the question may hold: kept beside the caps, so the save gate uses it too. */
export { optionCapOf } from './task-flow-draft.js';

/** The bounds as the engine reads them. */
export const manyBounds = (many, count) => ({
  min: many?.min ?? 1,
  max: many?.max ?? count,
});

/** Only the bounds that say something: a least above 1, a most below every choice. */
const written = (min, max, count) => ({
  ...(min > 1 ? { min } : {}),
  ...(max < count ? { max } : {}),
});

/** `many` with one bound set: a least never above the most, a most never below the least. */
export const withBound = (many, bound, value, count) => {
  const { min, max } = manyBounds(many, count);
  return bound === 'min'
    ? written(value, Math.max(max, value), count)
    : written(Math.min(min, value), value, count);
};

/** `many` after the choices changed to `count`: a bound past them is dropped, the least kept under the most. */
export const clampMany = (many, count) => {
  if (!many) return many;
  const max = Math.min(many.max ?? count, count);
  return written(Math.min(many.min ?? 1, max), max, count);
};
